import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import jwtAxios, { setAuthToken } from "./index";
import { AuthUser, ServerUser } from "../../../../types/models/AuthUser";
import {
  fetchError,
  fetchStart,
  fetchSuccess,
} from "../../../../redux/actions";
import { authRole } from "../../../../shared/constants/AppConst";

interface JWTAuthContextProps {
  user: AuthUser | null | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface SignUpProps {
  name: string;
  email: string;
  password: string;
}

interface SignInProps {
  email: string;
  password: string;
}

interface JWTAuthActionsProps {
  signUpUser: (data: SignUpProps) => void;
  signInUser: (data: SignInProps) => void;
  logout: () => void;
}

const JWTAuthContext = createContext<JWTAuthContextProps>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
});
const JWTAuthActionsContext = createContext<JWTAuthActionsProps>({
  signUpUser: () => {},
  signInUser: () => {},
  logout: () => {},
});

export const useJWTAuth = () => useContext(JWTAuthContext);

export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

interface JWTAuthAuthProviderProps {
  children: ReactNode;
}

const JWTAuthAuthProvider: React.FC<JWTAuthAuthProviderProps> = ({
  children,
}) => {
  const [firebaseData, setJWTAuthData] = useState<JWTAuthContextProps>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    const getAuthUser = () => {
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");

      if (!token) {
        setJWTAuthData({
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
        });
        return;
      }

      setAuthToken(token);
      if (user) {
        setJWTAuthData(JSON.parse(user));
        return;
      }

      setJWTAuthData({
              user: undefined,
              isLoading: false,
              isAuthenticated: false,
            });
      // setJWTAuthData(
      // jwtAxios
      //   .get("/auth")
      //   .then(({ data }) =>
      //     setJWTAuthData({
      //       user: data,
      //       isLoading: false,
      //       isAuthenticated: true,
      //     })
      //   )
      //   .catch(() =>
      //     setJWTAuthData({
      //       user: undefined,
      //       isLoading: false,
      //       isAuthenticated: false,
      //     })
      //   );
    };

    getAuthUser();
  }, []);

  const signInUser = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    dispatch(fetchStart());
    try {
      const { data } = await jwtAxios.post("private/login", { username: email, password });
      // const { data } = await jwtAxios.post("auth", { email, password });
      console.warn(`User login ${JSON.stringify(data)}`);
      localStorage.setItem("token", data.token);
      setAuthToken(data.token);
      const res = await jwtAxios.get('private/user/profile');
      // const res = await jwtAxios.get("/auth");
      console.info(`User profile ${JSON.stringify(res?.data)}`);
      localStorage.setItem("profile", JSON.stringify(res?.data));
      const authUser = {
        user: getUserObject(res.data),
        isAuthenticated: true,
        isLoading: false,
      };
      localStorage.setItem("user", JSON.stringify(authUser));
      setJWTAuthData(authUser);
      dispatch(fetchSuccess());
    } catch (error) {
      setJWTAuthData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch(fetchError("Something went wrong"));
    }
  };

  const getUserObject = (authUser: ServerUser): AuthUser => {
    let role = ["user"];
    if (Array.isArray(authUser.groups)) {
      // Check if groups is an array of objects with name property or an array of strings
      if (typeof authUser.groups[0] === 'object') {
        // Array of objects with name property
        if (authUser.groups.some(group => group.name === 'ADMIN')) {
          role.push("admin");
        }
        if (authUser.groups.some(group => group.name === 'PIM')) {
          role.push("pim");
        }
      } else {
        // Array of strings
        // @ts-ignore
        if (authUser.groups.includes('ADMIN')) {
          role.push("admin");
        }
        // @ts-ignore
        if (authUser.groups.includes('PIM')) {
          role.push("pim");
        }
      }
    }
    return {
      displayName: `${authUser.firstName} ${authUser.lastName}`,
      email: authUser.emailAddress,
      role: role,
      // token: authUser.id,
      id: Number(authUser.id),
      uid: authUser.id
    };
  };

  const signUpUser = async ({
    name,
    email,
    password,
  }: {
    name: string;
    email: string;
    password: string;
  }) => {
    dispatch(fetchStart());
    try {
      const { data } = await jwtAxios.post("users", { name, email, password });
      localStorage.setItem("token", data.token);
      setAuthToken(data.token);
      const res = await jwtAxios.get("/auth");
      setJWTAuthData({
        user: res.data,
        isAuthenticated: true,
        isLoading: false,
      });
      dispatch(fetchSuccess());
    } catch (error) {
      setJWTAuthData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch(fetchError("Something went wrong"));
    }
  };

  const logout = async () => {
    localStorage.removeItem("token");
    localStorage.removeItem("profile");
    setAuthToken(null);
    setJWTAuthData({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
  };

  return (
    <JWTAuthContext.Provider
      value={{
        ...firebaseData,
      }}
    >
      <JWTAuthActionsContext.Provider
        value={{
          signUpUser,
          signInUser,
          logout,
        }}
      >
        {children}
      </JWTAuthActionsContext.Provider>
    </JWTAuthContext.Provider>
  );
};
export default JWTAuthAuthProvider;
